/**
 * @parkify/api-client: the one fetch wrapper the Parkify SPAs share.
 *
 * Before this package every app owned its own copy of the same five decisions
 * (which host, which credential, how a body is parsed, what a failure looks
 * like, what a 401 means) and they drifted apart one fix at a time. This module
 * owns those decisions; an app's `api.ts` keeps only what is genuinely its own
 * (patrol's credential order, the tenant read-only lens, consumer's CSRF
 * bootstrap and deadlines).
 *
 * Where two apps behaved differently on purpose, the difference is an option
 * here, not a second wrapper. Each option says which app relies on it.
 *
 * ZERO runtime dependencies and no import of `import.meta.env`: the app reads
 * its own env and passes it in, so this file typechecks and tests the same way
 * under every app's tsc and under node.
 *
 * NO request-id header. No app sends one today and the Worker reads none
 * (`X-Parkify-*` headers are auth, not tracing). Adding a custom header to a
 * plain GET also turns it into a CORS preflight on every call, so it would need
 * the Worker's allow-headers list first. Callers that need an idempotency key
 * send it in the body, as the routes already expect.
 */

/** The only production host for the Pay API Worker. Its workers.dev URL was
 *  disabled in #3455 and now 404s, so nothing may fall back to it. */
export const PARKIFY_API_ORIGIN = 'https://api.parkify.com'

/** The env fields an app passes to {@link resolveApiBase}. */
export interface ApiBaseEnv {
  /** Explicit base, e.g. a local `wrangler dev` or staging. Always wins, even ''. */
  VITE_API_BASE?: string
  /** ops' older name for the same thing: the payments route prefix, which is
   *  stripped. Only read when VITE_API_BASE is unset. */
  VITE_PAYMENT_API_BASE?: string
  /** `import.meta.env.DEV`. Only read with `devSameOrigin`. */
  DEV?: boolean
}

/**
 * The API origin for this build, with no trailing path.
 *
 * `devSameOrigin` (consumer) answers '' under `vite` dev so calls stay on the
 * dev server's origin, where its stub middleware intercepts them. Apps whose dev
 * server has no API stub leave it off and keep the production default.
 */
export function resolveApiBase(env: ApiBaseEnv, opts: { devSameOrigin?: boolean } = {}): string {
  if (env.VITE_API_BASE !== undefined) return env.VITE_API_BASE
  if (env.VITE_PAYMENT_API_BASE !== undefined) return env.VITE_PAYMENT_API_BASE.replace('/api/payments', '')
  if (opts.devSameOrigin && env.DEV) return ''
  return PARKIFY_API_ORIGIN
}

/**
 * Every failure the client reports. One shape for every app:
 *
 * - `status`: the HTTP status, or 0 when no response arrived (`offline`), the
 *   browser's own convention. A number either way, so callers that compare it
 *   never have to handle null.
 * - `code`: the server's structured `code`, when it sent one. Branch on this,
 *   never on `message`.
 * - `body`: the parsed JSON error body, when it was an object. `data` is the
 *   same object under consumer's older name.
 *
 * The positional constructor matches consumer's original `ApiError`, so its
 * existing `new ApiError(message, status, code, data)` calls keep working.
 */
export class ApiError extends Error {
  readonly status: number
  // `declare`: an absent code or body stays absent (`'code' in err` is false),
  // as on tenant-portal's old Object.assign error, instead of an own undefined.
  declare readonly code?: string
  declare readonly body?: Record<string, unknown>
  /** True when no response arrived at all: no signal, DNS, a dropped connection. */
  readonly offline: boolean

  constructor(
    message: string,
    status: number,
    code?: string,
    body?: Record<string, unknown>,
    opts: { offline?: boolean } = {},
  ) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    if (code !== undefined) Object.defineProperty(this, 'code', { value: code, enumerable: true })
    if (body !== undefined) Object.defineProperty(this, 'body', { value: body, enumerable: true })
    this.offline = opts.offline ?? false
  }

  /** consumer's name for {@link body}. */
  get data(): Record<string, unknown> | undefined {
    return this.body
  }
}

/** `code` on an ApiError whose 2xx body was not JSON (a captive portal, a proxy page). */
export const INVALID_JSON_CODE = 'invalid_json'

/** What a request handed to the hooks looks like. */
export interface RequestContext {
  /** The path as the caller passed it, before the base is prefixed. */
  path: string
  method: string
  /** Whether this call carried a bearer credential. */
  authenticated: boolean
}

export interface ErrorMessageInput {
  status: number
  /** The response's status text ('' over HTTP/2). */
  statusText: string
  /** The parsed JSON body when it was an object. */
  body: Record<string, unknown> | undefined
  /** The raw response text ('' when unreadable). */
  text: string
}

export interface ApiClientOptions {
  /** Prefixed to every path. Usually `resolveApiBase(import.meta.env)`. */
  baseUrl: string
  /**
   * The fetch to call. Defaults to whatever `globalThis.fetch` is AT CALL TIME,
   * never a copy taken at construction: ops patches `window.fetch` after boot
   * (its 401 and view-as guards) and every test suite stubs it per test.
   */
  fetch?: typeof fetch
  /** Default `credentials` for every call. consumer sends 'include' so the
   *  `.parkify.com` session cookie reaches api.parkify.com. */
  credentials?: RequestCredentials
  /** Default `cache` for every call. patrol sends 'no-store': a cached
   *  "expired" is a wrongful citation. */
  cache?: RequestCache
  /**
   * Extra headers for every call, resolved per request (consumer's CSRF token
   * lives in storage and can change between calls). Per-call headers win.
   */
  headers?: (ctx: RequestContext) => HeadersInit | undefined | Promise<HeadersInit | undefined>
  /**
   * Called on a 401 before the error is thrown. tenant-portal drops to sign-in
   * when an authenticated call 401s; patrol forgets the credential that failed.
   * Never called for a 401 on a call that sent no credential unless the hook
   * itself chooses to act on `ctx.authenticated === false`.
   */
  onUnauthorized?: (res: Response, ctx: RequestContext) => void
  /**
   * The message for a failure whose body carries no usable `error` string.
   * Defaults to `Request failed (<status>)` (consumer). tenant-portal uses the
   * raw text, then `HTTP <status>`; patrol words it for an officer.
   */
  errorMessage?: (input: ErrorMessageInput) => string
  /**
   * When fetch itself rejects (no response arrived), throw an ApiError with
   * `offline: true` instead of the browser's TypeError. patrol turns this on:
   * the engine's wording ("Failed to fetch", "Load failed") is not copy.
   * consumer and tenant-portal leave it off and keep the raw rejection.
   * An abort is always rethrown untouched either way.
   */
  wrapNetworkErrors?: boolean
  /**
   * How to treat a 2xx whose body is empty or not JSON.
   * 'null' (consumer): resolve null, the old `jsonOrThrow` contract.
   * 'throw' (default; patrol, tenant-portal): an ApiError with
   * `code: 'invalid_json'`, so a captive portal's HTML is a failed read rather
   * than a SyntaxError escaping into copy.
   */
  invalidJson?: 'null' | 'throw'
}

export interface RequestOptions extends Omit<RequestInit, 'body' | 'headers'> {
  /** Serialised with JSON.stringify and sent with `Content-Type: application/json`. */
  json?: unknown
  /** A raw body (a Blob, a pre-serialised string). Sent as-is. */
  body?: BodyInit | null
  headers?: HeadersInit
  /** Sent as `Authorization: Bearer <token>`. Null/undefined sends none. */
  bearer?: string | null
  /** Per-call 401 hook. Replaces the client's; `false` suppresses it. */
  onUnauthorized?: ((res: Response, ctx: RequestContext) => void) | false
  /** Per-call override of the client option. */
  invalidJson?: 'null' | 'throw'
  /** Per-call override of the client's fallback wording. */
  errorMessage?: (input: ErrorMessageInput) => string
}

/**
 * A prefix-only caller: `baseUrl + path`, `init` passed through untouched, and
 * the Response handed back for EVERY status, never thrown.
 *
 * For code whose error handling is its own and must stay exactly as written:
 * the ops console's ~400 call sites each branch on `res.ok` / `res.status` and
 * read the body their own way. Moving them onto `request()` would change what
 * every one of them throws, so they move here first and change nothing but
 * where the base URL comes from. `request()`/`raw()` remain the target for a
 * site that wants the shared error shape.
 *
 * Deliberately a standalone function, not a method on {@link createApiClient}'s
 * object: it tree-shakes to a few bytes, and ops' entry chunk has under 1 kB of
 * budget headroom. Like the client, it resolves `globalThis.fetch` at call time
 * (ops patches `window.fetch` for its 401 and view-as guards) and forwards the
 * arguments as given, so `caller(path)` calls `fetch(url)` with one argument.
 */
export function createCaller(
  baseUrl: string,
  fetchImpl?: typeof fetch,
): (path: string, init?: RequestInit) => Promise<Response> {
  return (path, init) => {
    const f = fetchImpl ?? globalThis.fetch
    const url = `${baseUrl}${path}`
    return init === undefined ? f(url) : f(url, init)
  }
}

export interface ApiClient {
  readonly baseUrl: string
  /** `baseUrl + path`, for the places that need a URL rather than a call
   *  (an `<img src>`, a link handed to another app). */
  url(path: string): string
  /** Parse the 2xx body as JSON and resolve it. Throws {@link ApiError} otherwise. */
  request<T>(path: string, opts?: RequestOptions): Promise<T>
  /** Resolve the raw Response for a 2xx (a CSV, a blob). Throws {@link ApiError} otherwise. */
  raw(path: string, opts?: RequestOptions): Promise<Response>
}

function isAbort(e: unknown, signal: AbortSignal | null | undefined): boolean {
  if (signal?.aborted) return true
  return typeof e === 'object' && e !== null && (e as { name?: unknown }).name === 'AbortError'
}

function parseObject(text: string): Record<string, unknown> | undefined {
  if (!text) return undefined
  try {
    const v: unknown = JSON.parse(text)
    return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : undefined
  } catch {
    return undefined
  }
}

/**
 * Merge header sources into a plain record, last writer wins, case-insensitively.
 * A plain object rather than a `Headers` instance on purpose: it keeps the
 * casing callers wrote (`Authorization`, `Content-Type`) and every app's
 * existing tests read `init.headers` as a record.
 */
function mergeHeaders(...sources: (HeadersInit | undefined)[]): Record<string, string> {
  const out: Record<string, string> = {}
  const set = (k: string, v: string) => {
    for (const existing of Object.keys(out)) if (existing.toLowerCase() === k.toLowerCase()) delete out[existing]
    out[k] = v
  }
  for (const src of sources) {
    if (!src) continue
    if (src instanceof Headers) src.forEach((v, k) => set(k, v))
    else if (Array.isArray(src)) for (const [k, v] of src) set(k, v)
    else for (const [k, v] of Object.entries(src)) set(k, v)
  }
  return out
}

const hasHeader = (h: Record<string, string>, name: string) =>
  Object.keys(h).some(k => k.toLowerCase() === name.toLowerCase())

/**
 * The body as text. A Response always has `text()`; a response-shaped object
 * may carry only `json()`, which is how ~150 of the apps' test suites fake
 * fetch (`{ ok: true, json: async () => ... }`). Reading through `json()` there
 * and re-serialising keeps every parse decision in one place.
 */
async function bodyText(res: Response): Promise<string> {
  if (typeof res.text === 'function') return res.text()
  if (typeof (res as { json?: unknown }).json === 'function') {
    const v: unknown = await res.json()
    return v === undefined ? '' : JSON.stringify(v)
  }
  return ''
}

const defaultErrorMessage = ({ status }: ErrorMessageInput): string => `Request failed (${status})`

export function createApiClient(options: ApiClientOptions): ApiClient {
  const baseUrl = options.baseUrl
  const doFetch: typeof fetch = options.fetch ?? ((input, init) => globalThis.fetch(input, init))
  const errorMessage = options.errorMessage ?? defaultErrorMessage

  async function send(path: string, opts: RequestOptions): Promise<Response> {
    const { json, body, headers: callHeaders, bearer, onUnauthorized, invalidJson: _ij, errorMessage: callMessage, ...init } = opts
    const method = (init.method ?? 'GET').toUpperCase()
    const ctx: RequestContext = { path, method, authenticated: !!bearer }

    const headers = mergeHeaders(
      options.headers ? await options.headers(ctx) : undefined,
      callHeaders,
      bearer ? { Authorization: `Bearer ${bearer}` } : undefined,
    )
    let payload: BodyInit | null | undefined = body
    if (json !== undefined) {
      payload = JSON.stringify(json)
      if (!hasHeader(headers, 'Content-Type')) headers['Content-Type'] = 'application/json'
    }

    // An empty header set is left off entirely, so a bare call's init is just
    // what the caller passed (several suites assert on it).
    const reqInit: RequestInit = Object.keys(headers).length ? { ...init, headers } : { ...init }
    if (payload !== undefined) reqInit.body = payload
    if (reqInit.credentials === undefined && options.credentials) reqInit.credentials = options.credentials
    if (reqInit.cache === undefined && options.cache) reqInit.cache = options.cache

    let res: Response
    try {
      res = await doFetch(`${baseUrl}${path}`, reqInit)
    } catch (e) {
      if (isAbort(e, init.signal) || !options.wrapNetworkErrors) throw e
      throw new ApiError('No connection', 0, undefined, undefined, { offline: true })
    }

    if (res.ok) return res

    if (res.status === 401) {
      const hook = onUnauthorized === undefined ? options.onUnauthorized : onUnauthorized
      if (hook) hook(res, ctx)
    }
    const text = await bodyText(res).catch(() => '')
    const obj = parseObject(text)
    const said = obj?.error
    const message = typeof said === 'string' && said.trim()
      ? said
      : (callMessage ?? errorMessage)({ status: res.status, statusText: res.statusText ?? '', body: obj, text })
    const code = typeof obj?.code === 'string' ? obj.code : undefined
    throw new ApiError(message, res.status, code, obj)
  }

  return {
    baseUrl,
    url: path => `${baseUrl}${path}`,
    raw: (path, opts = {}) => send(path, opts),
    async request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
      const res = await send(path, opts)
      let text: string
      try {
        text = await bodyText(res)
      } catch (e) {
        if (isAbort(e, opts.signal)) throw e
        text = ''
      }
      if (text) {
        try {
          return JSON.parse(text) as T
        } catch { /* fall through to the invalid-JSON policy */ }
      }
      if ((opts.invalidJson ?? options.invalidJson ?? 'throw') === 'null') return null as T
      throw new ApiError('Parkify sent an unreadable answer', res.status, INVALID_JSON_CODE)
    },
  }
}
